import type { ImportSource, Project } from '@servicetim/shared';
import { useCreateImport, useUploadImportCsv } from '@servicetim/ui/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ProblemBanner,
  toast,
} from '@servicetim/ui/components';
import { useId, useState } from 'react';
import { FormRow } from '../projects/ui/form-row';
import { SimpleSelect } from '../projects/ui/simple-select';
import { xhrUpload } from '../tickets/attachments/upload-machine';
import { parseCsvHeaderLine } from './csv-headers';

export interface NewImportWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: readonly Project[];
  onCreated: (importId: string) => void;
}

const SOURCE_GUIDANCE: Record<ImportSource, string> = {
  jsm: 'Pulls issues live from the Jira Service Management Cloud REST API with a stored API token.',
  monday: 'Pulls items from one Monday.com board through its GraphQL API with a stored API token.',
  csv: 'Upload a CSV export; columns are parsed and typed automatically.',
  fixture: 'Development fixture — not a real source.',
};

/** The CSV column roles the wizard can assign, in the order shown. */
const CSV_ROLES: { key: string; label: string }[] = [
  { key: 'key', label: 'Key (external id)' },
  { key: 'summary', label: 'Summary' },
  { key: 'description', label: 'Description' },
  { key: 'status', label: 'Status' },
  { key: 'issueType', label: 'Issue type' },
  { key: 'priority', label: 'Priority' },
  { key: 'reporterEmail', label: 'Reporter email' },
  { key: 'assigneeEmail', label: 'Assignee email' },
  { key: 'createdAt', label: 'Created at' },
  { key: 'updatedAt', label: 'Updated at' },
  { key: 'labels', label: 'Labels' },
];

const NONE = '';

/**
 * "New import" wizard (S-09-10): source → credentials/upload → target project. CSV column
 * roles (`options.csvColumns`) are collected here, before the batch even exists — there is no
 * endpoint to set them afterward, only `POST /imports`'s own `options` (see
 * `packages/jobs/src/imports/csv/index.ts`) — so the header line is read client-side
 * (`csv-headers.ts`) the moment a file is chosen, well before the server ever parses the file.
 */
export function NewImportWizard({ open, onOpenChange, projects, onCreated }: NewImportWizardProps) {
  const id = useId();
  const createImport = useCreateImport();
  const uploadCsv = useUploadImportCsv();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [source, setSource] = useState<ImportSource>('csv');
  const [credentialsSecretArn, setCredentialsSecretArn] = useState('');
  const [jsmProjectKey, setJsmProjectKey] = useState('');
  const [mondayBoardId, setMondayBoardId] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRoles, setCsvRoles] = useState<Record<string, string>>({});
  const [target, setTarget] = useState<'existing' | 'create'>('existing');
  const [projectId, setProjectId] = useState('');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const reset = () => {
    setStep(1);
    setSource('csv');
    setCredentialsSecretArn('');
    setJsmProjectKey('');
    setMondayBoardId('');
    setCsvFile(null);
    setCsvHeaders([]);
    setCsvRoles({});
    setTarget('existing');
    setProjectId('');
    setUploadProgress(null);
    setSubmitError(null);
    createImport.reset();
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setCsvFile(file);
    setCsvHeaders(parseCsvHeaderLine(await file.text()));
  };

  const step2Valid =
    source === 'csv'
      ? csvFile !== null
      : source === 'jsm'
        ? credentialsSecretArn.trim() !== '' && jsmProjectKey.trim() !== ''
        : credentialsSecretArn.trim() !== '' && mondayBoardId.trim() !== '';
  const step3Valid = target === 'create' || projectId !== '';

  const submit = async () => {
    setSubmitError(null);
    try {
      const options: Record<string, unknown> =
        source === 'jsm'
          ? { jsmProjectKey: jsmProjectKey.trim() }
          : source === 'monday'
            ? { mondayBoardId: mondayBoardId.trim() }
            : {
                csvColumns: Object.fromEntries(
                  Object.entries(csvRoles).filter(([, header]) => header !== NONE),
                ),
              };
      const batch = await createImport.mutateAsync({
        source,
        projectId: target === 'existing' ? projectId : null,
        createProjects: target === 'create',
        credentialsSecretArn: source === 'csv' ? null : credentialsSecretArn.trim(),
        options,
      });
      if (source === 'csv' && csvFile) {
        const presigned = await uploadCsv.mutateAsync({
          importId: batch.id,
          filename: csvFile.name,
          contentType: csvFile.type || 'text/csv',
          sizeBytes: csvFile.size,
        });
        await xhrUpload({ url: presigned.url, fields: presigned.fields }, csvFile, (fraction) =>
          setUploadProgress(fraction),
        );
      }
      toast.success('Import created');
      reset();
      onOpenChange(false);
      onCreated(batch.id);
    } catch (err) {
      setSubmitError(err);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>New import — step {step} of 3</DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'Choose where the tickets are coming from.'
              : step === 2
                ? source === 'csv'
                  ? 'Upload the file and, optionally, tell us which columns are special.'
                  : 'A Secrets Manager reference to the API token, stored by an admin — never pasted here.'
                : 'Choose the ServiceTim project the tickets land in.'}
          </DialogDescription>
        </DialogHeader>

        {submitError ? <ProblemBanner error={submitError} size="sm" /> : null}

        {step === 1 ? (
          <fieldset className="space-y-3">
            <legend className="sr-only">Source</legend>
            {(['csv', 'jsm', 'monday'] as const).map((s) => (
              <label
                key={s}
                className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 hover:bg-surface-sunken has-[:checked]:border-brand-400 has-[:checked]:bg-accent-soft"
              >
                <input
                  type="radio"
                  name={`${id}-source`}
                  value={s}
                  checked={source === s}
                  onChange={() => setSource(s)}
                  className="mt-1"
                />
                <span>
                  <span className="block font-medium">
                    {s === 'csv'
                      ? 'CSV file'
                      : s === 'jsm'
                        ? 'Jira Service Management'
                        : 'Monday.com'}
                  </span>
                  <span className="block text-fg-muted text-sm">{SOURCE_GUIDANCE[s]}</span>
                </span>
              </label>
            ))}
          </fieldset>
        ) : null}

        {step === 2 && source === 'csv' ? (
          <div className="space-y-4">
            <FormRow label="CSV file" htmlFor={`${id}-file`}>
              <Input
                id={`${id}-file`}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => void onFile(e.target.files?.[0])}
              />
            </FormRow>
            {csvHeaders.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {CSV_ROLES.map((r) => (
                  <FormRow key={r.key} label={r.label} htmlFor={`${id}-role-${r.key}`}>
                    <SimpleSelect
                      id={`${id}-role-${r.key}`}
                      aria-label={r.label}
                      value={csvRoles[r.key] ?? NONE}
                      emptyLabel="(none)"
                      onChange={(v) => setCsvRoles((prev) => ({ ...prev, [r.key]: v ?? NONE }))}
                      options={csvHeaders.map((h) => ({ value: h, label: h }))}
                    />
                  </FormRow>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {step === 2 && source !== 'csv' ? (
          <div className="space-y-4">
            <FormRow
              label="Credentials Secrets Manager ARN"
              htmlFor={`${id}-arn`}
              hint="The API token must already be stored in Secrets Manager by an admin — it is never pasted into this wizard."
              required
            >
              <Input
                id={`${id}-arn`}
                value={credentialsSecretArn}
                onChange={(e) => setCredentialsSecretArn(e.target.value)}
                placeholder="arn:aws:secretsmanager:..."
              />
            </FormRow>
            {source === 'jsm' ? (
              <FormRow label="JSM project key" htmlFor={`${id}-jsm-key`} required>
                <Input
                  id={`${id}-jsm-key`}
                  value={jsmProjectKey}
                  onChange={(e) => setJsmProjectKey(e.target.value)}
                  placeholder="SUP"
                />
              </FormRow>
            ) : (
              <FormRow label="Monday.com board id" htmlFor={`${id}-board-id`} required>
                <Input
                  id={`${id}-board-id`}
                  value={mondayBoardId}
                  onChange={(e) => setMondayBoardId(e.target.value)}
                />
              </FormRow>
            )}
          </div>
        ) : null}

        {step === 3 ? (
          <fieldset className="space-y-3">
            <legend className="sr-only">Target</legend>
            <label className="flex items-start gap-3 rounded-md border border-border p-3">
              <input
                type="radio"
                name={`${id}-target`}
                checked={target === 'existing'}
                onChange={() => setTarget('existing')}
                className="mt-1"
              />
              <span className="w-full">
                <span className="block font-medium">Existing project</span>
                <SimpleSelect
                  aria-label="Target project"
                  value={projectId || undefined}
                  onChange={(v) => setProjectId(v ?? '')}
                  disabled={target !== 'existing'}
                  options={projects.map((p) => ({ value: p.id, label: `${p.name} (${p.key})` }))}
                  className="mt-2"
                />
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-md border border-border p-3">
              <input
                type="radio"
                name={`${id}-target`}
                checked={target === 'create'}
                onChange={() => setTarget('create')}
                className="mt-1"
              />
              <span>
                <span className="block font-medium">Create projects from source</span>
                <span className="block text-fg-muted text-sm">
                  Not yet supported by the import framework — discovery will fail with a clear error
                  if chosen.
                </span>
              </span>
            </label>
          </fieldset>
        ) : null}

        {uploadProgress !== null && uploadProgress < 1 ? (
          <p className="text-fg-muted text-sm">Uploading… {Math.round(uploadProgress * 100)}%</p>
        ) : null}

        <DialogFooter>
          {step > 1 ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep((s) => (s - 1) as 1 | 2)}
            >
              Back
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {step < 3 ? (
            <Button
              type="button"
              onClick={() => setStep((s) => (s + 1) as 2 | 3)}
              disabled={step === 1 ? false : !step2Valid}
            >
              Next
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => void submit()}
              disabled={!step3Valid || createImport.isPending || uploadCsv.isPending}
            >
              Create
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
